import { Button, Dialog, Flex, Text } from '@/components/ui';
import React, { useState } from 'react';
import {
  IconBuilding,
  IconChevronDown,
  IconChevronRight,
  IconDatabase,
  IconForms,
  IconTable,
  IconUser,
  IconX,
} from '@tabler/icons-react';

// Types

interface ColumnClassificationModalProps {
  columnName: string;
  filesCount: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClassifyAsMetadata: (metadataType: string, fieldType?: string) => void;
  onClassifyAsFormData: () => void;
  isSubform?: boolean; // Whether the current form is a subform
}

export const ColumnClassificationModal: React.FC<
  ColumnClassificationModalProps
> = ({
  columnName,
  filesCount,
  open,
  onOpenChange,
  onClassifyAsMetadata,
  onClassifyAsFormData,
  isSubform = false,
}) => {
  const [showMetadataTypes, setShowMetadataTypes] = useState(false);

  const handleFormData = () => {
    onClassifyAsFormData();
    onOpenChange(false);
  };

  // Define metadata types with grouping (conditional for subforms)
  const metadataGroups = [
    {
      title: 'Subject Data',
      icon: <IconUser color="var(--orange-9)" />,
      types: [
        {
          type: 'externalSubjectId',
          label: 'External Subject ID',
          description:
            'Required: Determines which subject the form data belongs to',
          required: true,
        },
        {
          type: 'subjectNumber',
          label: 'Subject Number',
          description: 'Required: Dictates subject numbering for new subjects',
          required: true,
        },
      ],
    },
    {
      title: 'Site Data',
      icon: <IconBuilding color="var(--purple-9)" />,
      types: [
        {
          type: 'siteNumber',
          label: 'Site Number',
          description: 'Required: Identifies which site the subject belongs to',
          required: true,
        },
        {
          type: 'site',
          label: 'Site ID',
          description: 'Alternative site identifier',
          required: false,
        },
        {
          type: 'siteName',
          label: 'Site Name',
          description: 'Human-readable site name',
          required: false,
        },
      ],
    },
    {
      title: 'Form Data',
      icon: <IconForms color="var(--blue-9)" />,
      types: [
        {
          type: 'formId',
          label: 'Form ID',
          description:
            'Required: Ensures form uniqueness and enables reimports',
          required: true,
        },
        // Main Form ID only for subforms
        ...(isSubform
          ? [
              {
                type: 'mainFormId' as const,
                label: 'Main Form ID' as const,
                description:
                  'Required for subforms: Links to parent form' as const,
                required: true,
              },
            ]
          : []),
      ].filter(Boolean), // Filter out empty arrays
    },
    {
      title: 'Other',
      icon: <IconDatabase color="var(--gray-9)" />,
      types: [
        {
          type: 'subjectData',
          label: 'Subject Data',
          description: 'General subject information',
          required: false,
        },
        {
          type: 'deletedForm',
          label: 'Deleted Form',
          description: 'Mark form as deleted',
          required: false,
        },
        {
          type: 'formIndex',
          label: 'Form Index',
          description: 'Form sequence number',
          required: false,
        },
        {
          type: 'trash',
          label: 'Trash',
          description: 'Ignore this column',
          required: false,
        },
      ],
    },
  ].filter(group => group.types.length > 0); // Remove empty groups

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content style={{ maxWidth: '500px' }}>
        <Dialog.Title>
          <Flex align="center" justify="between" width="100%">
            <Text size="4" weight="bold">
              Classify Column
            </Text>

            <Button
              variant="surface"
              size="large"
              onClick={() => onOpenChange(false)}
              title="Close"
            >
              <IconX />
            </Button>
          </Flex>
        </Dialog.Title>

        <Dialog.Description>
          <Text size="2" color="gray">
            How should "{columnName}" be classified across {filesCount} file
            {filesCount > 1 ? 's' : ''}?
          </Text>
        </Dialog.Description>

        <Flex direction="column" gap="3" mt="4">
          {/* Form Data Option */}
          <Button
            variant="outlined"
            size="small"
            onClick={handleFormData}
            style={{ justifyContent: 'flex-start' }}
          >
            <Flex align="center" gap="3">
              <IconTable color="var(--green-9)" />
              <Flex direction="column" gap="1" align="start">
                <Text size="3" weight="medium">
                  Form Data
                </Text>
                <Text size="2" color="gray">
                  Data field (PATIENT_ID, DIAGNOSIS_CODE, etc.)
                </Text>
              </Flex>
            </Flex>
          </Button>

          {/* Metadata Option with Expandable Types */}
          <Flex direction="column" gap="2">
            <Button
              variant="outlined"
              size="large"
              onClick={() => setShowMetadataTypes(!showMetadataTypes)}
              style={{ justifyContent: 'flex-start' }}
            >
              <Flex align="center" gap="3" width="100%">
                <IconDatabase color="var(--blue-9)" />
                <Flex direction="column" gap="1" align="start" flexGrow="1">
                  <Flex align="center" gap="2">
                    <Text size="3" weight="medium">
                      Metadata
                    </Text>
                    {showMetadataTypes ? (
                      <IconChevronDown />
                    ) : (
                      <IconChevronRight />
                    )}
                  </Flex>
                  <Text size="2" color="gray">
                    System field (subject ID, site ID, form ID, etc.)
                  </Text>
                </Flex>
              </Flex>
            </Button>

            {/* Expanded Metadata Types */}
            {showMetadataTypes && (
              <Flex direction="column" gap="2" pl="6">
                {metadataGroups.map(group => (
                  <Flex key={group.title} direction="column" gap="1">
                    <Flex align="center" gap="2">
                      {group.icon}
                      <Text size="2" weight="medium" color="gray">
                        {group.title}
                      </Text>
                    </Flex>

                    {group.types.map(metadataType => (
                      <Button
                        key={metadataType.type}
                        variant="ghost"
                        size="medium"
                        onClick={() => {
                          onClassifyAsMetadata(metadataType.type);
                          onOpenChange(false);
                        }}
                        style={{
                          justifyContent: 'flex-start',
                          paddingLeft: '20px',
                        }}
                        title={metadataType.description}
                      >
                        <Flex align="center" gap="2" width="100%">
                          <Text size="2" weight="medium">
                            {metadataType.label}
                          </Text>
                          {metadataType.required && (
                            <Text size="2" color="red" weight="bold">
                              REQUIRED
                            </Text>
                          )}
                        </Flex>
                      </Button>
                    ))}
                  </Flex>
                ))}
              </Flex>
            )}
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default ColumnClassificationModal;
